import { useState } from "react";
import { searchLocal, lookupNutrients } from "../constants/nutrients";
import { cupGramsForIngredient, toGrams } from "../utils/calculations";
import { inp, btn } from "../styles/shared";

function readCache() {
  try { return JSON.parse(localStorage.getItem("fitlog_food_cache") || "{}"); } catch { return {}; }
}

// Build a deduplicated list of foods from: custom foods library, log history, local DB
function searchHistory(query, customFoods, logs) {
  const q = query.toLowerCase().trim();
  if (q.length < 2) return [];

  const results = [];
  const seen    = new Set();
  const cache   = readCache();

  // 1. Custom foods library — highest trust, explicit macros
  for (const food of (customFoods || [])) {
    if (!food.name) continue;
    if (!food.name.toLowerCase().includes(q)) continue;
    const key = food.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const local = lookupNutrients(food.name);
    results.push({
      name:     food.name,
      calories: food.calories || 0,
      protein:  food.protein  || 0,
      carbs:    food.carbs    || 0,
      fat:      food.fat      || 0,
      fiber:    food.fiber    || 0,
      _cupGrams: food._cupGrams ?? local?._cupGrams,
      _serving:  food._serving,
      per:      "per 100g",
      source:   "Custom",
    });
  }

  // 2. Previously logged ingredients — nutrients from _nutrients or cache
  for (const day of (logs || [])) {
    for (const meal of Object.values(day.meals || {})) {
      for (const ing of (meal.ingredients || [])) {
        if (!ing.name) continue;
        if (!ing.name.toLowerCase().includes(q)) continue;
        const key = ing.name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const local = lookupNutrients(ing.name);
        const n = ing._nutrients || cache[key] || local || null;
        results.push({
          name:     ing.name,
          calories: n?.calories || 0,
          protein:  n?.protein  || 0,
          carbs:    n?.carbs    || 0,
          fat:      n?.fat      || 0,
          fiber:    n?.fiber    || 0,
          _cupGrams: ing._cupGrams ?? n?._cupGrams ?? local?._cupGrams,
          _serving:  n?._serving,
          per:      n?._serving !== undefined ? "per serving" : "per 100g",
          source:   "History",
        });
      }
    }
  }

  // 3. Built-in nutrient constants — fallback
  for (const food of searchLocal(q)) {
    const key = food.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    results.push({ ...food, source: "Local" });
  }

  return results.slice(0, 10);
}

export default function IngredientEditor({ ingredients, onChange, customFoods = [], logs = [] }) {
  const [searchState, setSearchState] = useState({}); // { [index]: { query, results, open } }

  function handleNameChange(i, val) {
    const updated = [...ingredients];
    updated[i] = { ...updated[i], name: val };
    onChange(updated);
    if (val.trim().length < 2) {
      setSearchState(s => ({ ...s, [i]: { query: val, results: [], open: false } }));
      return;
    }
    const results = searchHistory(val, customFoods, logs);
    setSearchState(s => ({ ...s, [i]: { query: val, results, open: true } }));
  }

  function pickFood(i, food) {
    const updated = [...ingredients];
    const hasAmount = updated[i].amount !== "" && updated[i].amount != null;
    const defaultAmount = food._serving !== undefined ? food._serving : 100;
    const amount = hasAmount && !(food._serving !== undefined && Number(updated[i].amount) === 100)
      ? updated[i].amount
      : defaultAmount;
    updated[i] = {
      ...updated[i],
      name:       food.name,
      amount,
      unit:       updated[i].unit || "g",
      _cupGrams:  food._cupGrams ?? updated[i]._cupGrams,
      _nutrients: {
        calories: food.calories,
        protein: food.protein,
        carbs: food.carbs,
        fat: food.fat,
        fiber: food.fiber || 0,
        _cupGrams: food._cupGrams,
        _serving: food._serving,
      },
      _source:    food.source,
      _per:       food.per,
    };
    onChange(updated);
    setSearchState(s => ({ ...s, [i]: { query: food.name, results: [], open: false } }));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {ingredients.map((ing, i) => {
        const ss = searchState[i] || {};
        return (
          <div key={i} style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
              {/* Name input */}
              <div style={{ flex: 2, position: "relative" }}>
                <input
                  style={{ ...inp }}
                  value={ing.name}
                  placeholder="Type food name…"
                  onChange={e => handleNameChange(i, e.target.value)}
                  onFocus={() => { if (ing.name?.length >= 2) setSearchState(s => ({ ...s, [i]: { ...s[i], open: true } })); }}
                  onBlur={() => setTimeout(() => setSearchState(s => ({ ...s, [i]: { ...s[i], open: false } })), 200)}
                />
              </div>
              {/* Amount */}
              {(ing.unit || "g") === "cups" ? (
                <select
                  value={ing.amount}
                  onChange={e => { const u = [...ingredients]; u[i] = { ...ing, amount: e.target.value }; onChange(u); }}
                  style={{ ...inp, width: 90, flex: "0 0 90px", cursor: "pointer" }}
                >
                  <option value="0.25">¼ cup</option>
                  <option value="0.333">⅓ cup</option>
                  <option value="0.5">½ cup</option>
                  <option value="0.667">⅔ cup</option>
                  <option value="0.75">¾ cup</option>
                  <option value="1">1 cup</option>
                  <option value="1.25">1¼ cups</option>
                  <option value="1.5">1½ cups</option>
                  <option value="2">2 cups</option>
                </select>
              ) : (
                <input
                  style={{ ...inp, width: 65, flex: "0 0 65px" }}
                  type="number" value={ing.amount} placeholder="100"
                  onChange={e => { const u = [...ingredients]; u[i] = { ...ing, amount: e.target.value }; onChange(u); }}
                />
              )}
              <select
                value={ing.unit || "g"}
                onChange={e => {
                  const newUnit    = e.target.value;
                  const newAmt     = newUnit === "cups" ? "0.5" : ing.amount;
                  const cupGrams   = newUnit === "cups" ? cupGramsForIngredient(ing, ing._nutrients) : ing._cupGrams;
                  const u = [...ingredients]; u[i] = { ...ing, unit: newUnit, amount: newAmt, _cupGrams: cupGrams }; onChange(u);
                }}
                style={{ ...inp, width: 66, flex: "0 0 66px", padding: "6px 4px", cursor: "pointer" }}
              >
                <option value="g">g</option>
                <option value="ml">ml</option>
                <option value="cups">cups</option>
              </select>
              {/* Remove */}
              <button
                onClick={() => { onChange(ingredients.filter((_, j) => j !== i)); setSearchState(s => { const n = { ...s }; delete n[i]; return n; }); }}
                style={{ ...btn, background: "rgba(239,68,68,0.15)", color: "#ef4444", padding: "5px 9px", fontSize: 12, borderRadius: 8 }}
              >✕</button>
            </div>

            {/* Per-amount macro preview */}
            {(ing.unit || "g") === "cups" && (
              <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4, paddingLeft:2, flexWrap:"wrap" }}>
                <span style={{ fontSize:10, color:"#666", fontFamily:"'Space Mono',monospace" }}>g per cup:</span>
                <input
                  type="number" min="1"
                  value={cupGramsForIngredient(ing, ing._nutrients)}
                  onChange={e => { const currentCupGrams = cupGramsForIngredient(ing, ing._nutrients); const u=[...ingredients]; u[i]={...ing,_cupGrams:parseFloat(e.target.value)||currentCupGrams}; onChange(u); }}
                  style={{ ...inp, width:60, flex:"0 0 60px", fontSize:11, padding:"3px 6px" }}
                />
                <span style={{ fontSize:10, color:"#555", fontStyle:"italic", flexBasis:"100%" }}>oats≈80 · pea protein≈100 · whey≈120 · dry rice≈185 · liquids≈240</span>
              </div>
            )}

            {(() => {
              const localN = lookupNutrients(ing.name);
              const n = ing._nutrients || localN;
              if (!n) return null;
              const unit     = ing.unit || "g";
              const cupGrams = cupGramsForIngredient(ing, n);
              const amount = parseFloat(ing.amount);
              const amountValue = Number.isFinite(amount) ? amount : 100;
              const g = toGrams(amountValue, unit, cupGrams);
              const scale = n._serving !== undefined ? n._serving : 100;
              const cal  = Math.round((n.calories * g) / scale);
              const pro  = Math.round((n.protein  * g) / scale);
              const carb = Math.round((n.carbs    * g) / scale);
              const fat  = Math.round((n.fat      * g) / scale);
              const FRAC = { "0.25":"¼","0.333":"⅓","0.5":"½","0.667":"⅔","0.75":"¾","1":"1","1.25":"1¼","1.5":"1½","2":"2" };
              const fracStr = FRAC[String(ing.amount)] ?? amountValue;
              const label = n._serving !== undefined
                ? `${amountValue} serving${amountValue === 1 ? "" : "s"} →`
                : unit === "cups"
                ? `${fracStr} cup${amountValue > 1 ? "s" : ""} (${Math.round(g)}g) →`
                : unit === "ml" ? `${amountValue}ml →` : `${g}g →`;
              return (
                <div style={{ fontSize: 10, fontFamily: "'Space Mono',monospace", marginTop: 3, paddingLeft: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ color: "#999" }}>{label}</span>
                  <span style={{ color: "#f97316", fontWeight: 700 }}>{pro}g protein</span>
                  <span style={{ color: "#888" }}>{cal}kcal</span>
                  <span style={{ color: "#888" }}>{carb}g carb</span>
                  <span style={{ color: "#888" }}>{fat}g fat</span>
                  {ing._source && <span style={{ color: ing._source === "Custom" ? "#f97316" : ing._source === "History" ? "#a78bfa" : "#555" }}>{ing._source}</span>}
                </div>
              );
            })()}

            {/* Search dropdown */}
            {ss.open && ss.results?.length > 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                background: "#0f0f1a", border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10, overflow: "hidden", marginTop: 4, boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
              }}>
                {ss.results.map((food, fi) => (
                  <div
                    key={fi}
                    onMouseDown={() => pickFood(i, food)}
                    style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(249,115,22,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div>
                      <div style={{ fontSize: 13, color: "#e2e8f0", marginBottom: 2 }}>{food.name}</div>
                      <div style={{ fontSize: 10, fontFamily: "'Space Mono',monospace", color: "#999" }}>
                        {food.per} · {food.calories}kcal · {food.protein}g P · {food.carbs}g C · {food.fat}g F
                      </div>
                    </div>
                    <span style={{
                      fontSize: 9, fontFamily: "'Space Mono',monospace",
                      color: food.source === "Custom" ? "#f97316" : food.source === "History" ? "#a78bfa" : "#555",
                      background: "rgba(0,0,0,0.3)", padding: "2px 6px", borderRadius: 4, marginLeft: 10, flexShrink: 0,
                    }}>{food.source}</span>
                  </div>
                ))}
              </div>
            )}
            {ss.open && ss.results?.length === 0 && ss.query?.length >= 2 && (
              <div style={{
                position: "absolute", top: "100%", left: 0, zIndex: 100,
                background: "#0f0f1a", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10, padding: "10px 14px", marginTop: 4, fontSize: 12, color: "#888",
              }}>
                No match in your foods — add it via the Foods tab first
              </div>
            )}
          </div>
        );
      })}
      <button
        onClick={() => onChange([...ingredients, { name: "", amount: 100, unit: "g", _nutrients: null }])}
        style={{ ...btn, background: "rgba(255,255,255,0.05)", color: "#777", padding: "6px 13px", fontSize: 12, alignSelf: "flex-start" }}
      >+ Add ingredient</button>
    </div>
  );
}
